import { baseApi } from "./base.service";
import { RESOURCES } from "./resources";

export type NotificacaoFiltro = "all" | "nao_lidas" | "lidas";

export type NotificacaoItem = {
  id: number;
  loginId: number;
  orgTitulo: string | null;
  titulo: string | null;
  mensagem: string | null;
  link: string | null;
  categoria: string | null;
  grupoChave: string | null;
  status: number;
  dataCadastro: string | null;
  lida: boolean;
  atorUserId?: string | null;
  atorNome?: string | null;
  atorEmail?: string | null;
  checkinId?: number | null;
  checkinFotoId?: number | null;
  checkinFotoExtensao?: string | null;
  checkinFotoLoginId?: number | null;
};

export type NotificacaoGrupo = {
  grupoChave: string;
  loginId: number;
  orgTitulo: string | null;
  categoria: string | null;
  tituloResumo: string | null;
  mensagemResumo: string | null;
  link: string | null;
  naoLidas: number;
  total: number;
  itens: NotificacaoItem[];
  grupoDevocionalId?: number | null;
  grupoDevocionalTitulo?: string | null;
  grupoFotoId?: number | null;
  grupoFotoExtensao?: string | null;
  grupoFotoLoginId?: number | null;
  grupoDevocionalLoginId?: number | null;
  grupoDevocionalVisibilidade?: string | null;
  grupoDevocionalCategoria?: string | null;
};

export function notificacaoGrupoKey(grupo: Pick<NotificacaoGrupo, "loginId" | "grupoChave">): string {
  return `${grupo.loginId}:${grupo.grupoChave}`;
}

export type NotificacoesGroupedResponse = {
  grupos: NotificacaoGrupo[];
  total: number;
  page: number;
  perPage: number;
  totalPages: number;
  naoLidasTotal?: number;
};

export type DevocionalNotificacaoFlat = {
  item: NotificacaoItem;
  grupo: NotificacaoGrupo;
};

export type NotificacoesDevocionalItensResponse = {
  itens: DevocionalNotificacaoFlat[];
  total: number;
  page: number;
  perPage: number;
  totalPages: number;
  naoLidasTotal: number;
};

export type NotificacoesGrupoResponse = {
  grupoChave: string;
  loginId: number;
  naoLidas: number;
  notificacoes: NotificacaoItem[];
  total: number;
  page: number;
  perPage: number;
  totalPages: number;
};

const notificacoesTags = [
  { type: RESOURCES.NOTIFICACOES, id: "LIST" },
  { type: RESOURCES.NOTIFICACOES, id: "COUNT" },
] as const;

const notificacoesAPI = baseApi.injectEndpoints({
  endpoints: (build) => ({
    getNotificacoesGrouped: build.query<
      NotificacoesGroupedResponse,
      { page?: number; perPage?: number; filtro?: NotificacaoFiltro; categoria?: string }
    >({
      query: ({ page = 1, perPage = 30, filtro = "all", categoria }) => ({
        url: "/notificacoes/grouped",
        params: { page, perPage, filtro, ...(categoria ? { categoria } : {}) },
      }),
      providesTags: () => [{ type: RESOURCES.NOTIFICACOES, id: "LIST" }],
    }),
    getNotificacoesDevocionalItens: build.query<
      NotificacoesDevocionalItensResponse,
      { page?: number; perPage?: number; filtro?: NotificacaoFiltro }
    >({
      query: ({ page = 1, perPage = 20, filtro = "all" }) => ({
        url: "/notificacoes/devocional/itens",
        params: { page, perPage, filtro },
      }),
      providesTags: () => [{ type: RESOURCES.NOTIFICACOES, id: "DEVOCIONAL_ITENS" }],
    }),
    getNotificacoesByGrupo: build.query<
      NotificacoesGrupoResponse,
      {
        grupoChave: string;
        loginId: number;
        page?: number;
        perPage?: number;
        filtro?: NotificacaoFiltro;
      }
    >({
      query: ({ grupoChave, loginId, page = 1, perPage = 50, filtro = "all" }) => ({
        url: "/notificacoes/by-grupo",
        params: { grupoChave, loginId, page, perPage, filtro },
      }),
      providesTags: (_result, _error, arg) => [
        { type: RESOURCES.NOTIFICACOES, id: `GRUPO:${arg.loginId}:${arg.grupoChave}` },
        { type: RESOURCES.NOTIFICACOES, id: "COUNT" },
      ],
    }),
    getUnreadNotificationsCount: build.query<{ count: number }, void>({
      query: () => ({
        url: "/notificacoes/unread-count",
      }),
      providesTags: () => [{ type: RESOURCES.NOTIFICACOES, id: "COUNT" }],
    }),
    markNotificationAsRead: build.mutation<unknown, { id: number; loginId: number }>({
      query: ({ id, loginId }) => ({
        url: `/notificacoes/${id}/read`,
        method: "POST",
        body: { loginId },
      }),
      invalidatesTags: () => [{ type: RESOURCES.NOTIFICACOES }],
    }),
    markAllNotificationsAsRead: build.mutation<unknown, void>({
      query: () => ({
        url: "/notificacoes/mark-all-read",
        method: "POST",
      }),
      invalidatesTags: () => [{ type: RESOURCES.NOTIFICACOES }],
    }),
    markNotificationGroupAsRead: build.mutation<unknown, { grupoChave: string; loginId: number }>({
      query: ({ grupoChave, loginId }) => ({
        url: "/notificacoes/group/read",
        method: "POST",
        body: { grupoChave, loginId },
      }),
      invalidatesTags: () => [{ type: RESOURCES.NOTIFICACOES }],
    }),
    markDevocionalAvisosAsRead: build.mutation<unknown, void>({
      query: () => ({
        url: "/notificacoes/devocional/mark-read",
        method: "POST",
      }),
      invalidatesTags: () => [{ type: RESOURCES.NOTIFICACOES }],
    }),
  }),
});

export const {
  useGetNotificacoesGroupedQuery,
  useGetNotificacoesDevocionalItensQuery,
  useGetNotificacoesByGrupoQuery,
  useGetUnreadNotificationsCountQuery,
  useMarkNotificationAsReadMutation,
  useMarkAllNotificationsAsReadMutation,
  useMarkNotificationGroupAsReadMutation,
  useMarkDevocionalAvisosAsReadMutation,
} = notificacoesAPI;

export { notificacoesAPI };
